// --- Dependencies --------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyProduct } from "@/types";
import ProductCard from "@/UI/cards/ProductCard";
import Skeleton from "@/UI/atoms/Skeleton";
import ActionsBar from "@/UI/components/ActionsBar";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import FormProductPopup from "@/UI/popups/FormProductPopup";
import MoreActionsPopup from "@/UI/popups/MoreActionsPopup";
import SortProductsPopup from "@/UI/popups/SortProductsPopup";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

export default function CategoryProductsScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const { addComp, removeComp } = usePopup();
    const { canEdit } = usePermission();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const categoryName = (location.state as { name?: string } | null)?.name ?? "";

    const { data, loading, error, invoke } = useFetch<TyApiResponse<{ products: TyProduct[] }>>(`/menu/categories/${id}/products`);

    // Local products state — allows optimistic toggle updates without refetching the full list
    const [products, setProducts] = useState<TyProduct[]>([]);

    useEffect(() => {
        setProducts(data?.data?.products ?? []);
    }, [data]);

    // --- Delete ----------------------------------------------------------------------------------

    const [deletingId, setDeletingId] = useState<string | null>(null);

    const { data: deleteData, invoke: invokeDelete } = useFetch<TyApiResponse<Record<string, never>>>(
        `/menu/products/${deletingId ?? "x"}`,
        { method: "DELETE" },
        false
    );

    useEffect(() => {
        if (!deletingId) return;
        invokeDelete();
    }, [deletingId]);

    useEffect(() => {
        if (!deleteData?.success) return;
        setDeletingId(null);
        invoke();
    }, [deleteData]);

    // --- Toggle active ---------------------------------------------------------------------------

    const [togglingActiveId, setTogglingActiveId] = useState<string | null>(null);

    const { data: toggleActiveData, invoke: invokeToggleActive } = useFetch<TyApiResponse<unknown>>(
        `/menu/products/${togglingActiveId ?? "x"}/active`,
        { method: "PATCH" },
        false
    );

    useEffect(() => {
        if (!togglingActiveId) return;
        invokeToggleActive();
    }, [togglingActiveId]);

    useEffect(() => {
        if (!toggleActiveData?.success || !togglingActiveId) return;
        const toggledId = togglingActiveId;
        setProducts((prev) => prev.map((p) => (p._id === toggledId ? { ...p, active: !p.active } : p)));
        setTogglingActiveId(null);
    }, [toggleActiveData]);

    // --- Toggle hidden ---------------------------------------------------------------------------

    const [togglingHiddenId, setTogglingHiddenId] = useState<string | null>(null);

    const { data: toggleHiddenData, invoke: invokeToggleHidden } = useFetch<TyApiResponse<unknown>>(
        `/menu/products/${togglingHiddenId ?? "x"}/hidden`,
        { method: "PATCH" },
        false
    );

    useEffect(() => {
        if (!togglingHiddenId) return;
        invokeToggleHidden();
    }, [togglingHiddenId]);

    useEffect(() => {
        if (!toggleHiddenData?.success || !togglingHiddenId) return;
        const toggledId = togglingHiddenId;
        setProducts((prev) => prev.map((p) => (p._id === toggledId ? { ...p, hidden: !p.hidden } : p)));
        setTogglingHiddenId(null);
    }, [toggleHiddenData]);

    // --- Handlers --------------------------------------------------------------------------------

    const handleAdd = () => {
        addComp({
            name: "form-product",
            Component: FormProductPopup,
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
            details: { categoryId: id ?? "", onSuccess: invoke },
        });
    };

    const handleSort = () => {
        if (products.length < 2) return;
        addComp({
            name: "sort-products",
            Component: SortProductsPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: { categoryId: id ?? "", products, onSuccess: invoke },
        });
    };

    const handleEdit = (product: TyProduct) => {
        addComp({
            name: "form-product",
            Component: FormProductPopup,
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            details: { categoryId: id ?? "", onSuccess: invoke, editProductId: product._id },
        });
    };

    const handleCapture = (product: TyProduct) => {
        addComp({
            name: "form-product",
            Component: FormProductPopup,
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            details: { categoryId: id ?? "", onSuccess: invoke, editProductId: product._id, captureOnOpen: true },
        });
    };

    const handleDuplicate = (product: TyProduct) => {
        addComp({
            name: "form-product",
            Component: FormProductPopup,
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            details: { categoryId: id ?? "", onSuccess: invoke, duplicateProductId: product._id },
        });
    };

    const handleDeletePrompt = (product: TyProduct) => {
        removeComp("confirm-delete-product");
        addComp({
            name: "confirm-delete-product",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message: t.formProduct.deleteMessage,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.formProduct.deleteConfirm,
                onConfirm: () => setDeletingId(product._id),
            },
        });
    };

    const handleMore = (product: TyProduct, e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const name = `more-actions-${product._id}`;
        removeComp(name);
        addComp({
            name,
            Component: MoreActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            coverClose: true,
            anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
            details: {
                actions: [
                    { label: t.formProduct.editAction, icon: "/icons/edit-icon.svg", handler: () => handleEdit(product) },
                    {
                        label: t.formProduct.capturePicturesAction,
                        icon: "/icons/camera-brand-icon.svg",
                        handler: () => handleCapture(product),
                        disabled: product.pictures.length >= constants.maxProductPictures,
                    },
                    { label: t.formProduct.duplicateAction, icon: "/icons/duplicate-icon.svg", handler: () => handleDuplicate(product) },
                    { label: t.formProduct.deleteAction, icon: "/icons/delete-icon.svg", handler: () => handleDeletePrompt(product), danger: true },
                ],
            },
        });
    };

    const handleToggleActive = (product: TyProduct) => {
        if (togglingActiveId) return;
        setTogglingActiveId(product._id);
    };

    const handleToggleHidden = (product: TyProduct) => {
        if (togglingHiddenId) return;
        setTogglingHiddenId(product._id);
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <div className="category-products-screen">
            <ActionsBar
                title={categoryName}
                addLabel={t.menu.addLabel}
                onAdd={canEdit("menu") ? handleAdd : undefined}
                onSort={canEdit("menu") && products.length > 1 ? handleSort : undefined}
                onBack={() => navigate("/menu")}
            />
            <div className="category-products-screen-content">
                {loading && (
                    <div className="category-products-screen-grid">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton key={i} className="category-products-screen-skeleton" />
                        ))}
                    </div>
                )}
                {error && <p className="category-products-screen-message category-products-screen-message--error">{error}</p>}
                {!loading && !error && products.length === 0 && (
                    <div className="category-products-screen-empty">
                        <div className="category-products-screen-empty-icon">
                            <img src="/icons/price-icon.svg" alt="" width={32} height={32} />
                        </div>
                        <div className="category-products-screen-empty-text">
                            <p className="category-products-screen-empty-title">{t.menu.emptyProductsTitle}</p>
                            <p className="category-products-screen-empty-subtitle">{t.menu.emptyProductsSubtitle}</p>
                        </div>
                    </div>
                )}
                {!loading && !error && products.length > 0 && (
                    <div className="category-products-screen-grid">
                        {products.map((product) => (
                            <ProductCard
                                key={product._id}
                                product={product}
                                canEdit={canEdit("menu")}
                                onMore={canEdit("menu") ? (e) => handleMore(product, e) : undefined}
                                onToggleActive={canEdit("menu") ? () => handleToggleActive(product) : undefined}
                                onToggleHidden={canEdit("menu") ? () => handleToggleHidden(product) : undefined}
                                togglingActive={togglingActiveId === product._id}
                                togglingHidden={togglingHiddenId === product._id}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
